import React, { useState, useEffect } from 'react';
import impersonationService, { ImpersonationLog } from '../../services/impersonationService';
import {
  UserCircleIcon,
  ClockIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PlayIcon,
  StopIcon,
} from '@heroicons/react/24/outline';

const AdminImpersonationLogsPage: React.FC = () => {
  const [logs, setLogs] = useState<ImpersonationLog[]>([]);
  const [activeSessions, setActiveSessions] = useState<ImpersonationLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showStartModal, setShowStartModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState<ImpersonationLog | null>(null);
  const [showLogModal, setShowLogModal] = useState(false);
  
  const [startForm, setStartForm] = useState({
    targetUserId: '',
    reason: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [logsData, sessionsData] = await Promise.all([
        impersonationService.getLogs(),
        impersonationService.getActiveSessions(),
      ]);
      setLogs(logsData.sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()));
      setActiveSessions(sessionsData);
    } catch (error) {
      console.error('Failed to load impersonation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartImpersonation = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await impersonationService.start({ 
        targetUserId: startForm.targetUserId, 
        reason: startForm.reason 
      });
      alert(`Impersonation started. Token: ${result.token}`);
      setShowStartModal(false);
      setStartForm({ targetUserId: '', reason: '' });
      loadData();
    } catch (error) {
      console.error('Failed to start impersonation:', error);
      alert('Failed to start impersonation. Please try again.');
    }
  };

  const handleEndImpersonation = async (sessionId: string) => {
    if (!confirm('Are you sure you want to end this impersonation session?')) return;
    try {
      await impersonationService.end(sessionId);
      loadData();
    } catch (error) {
      console.error('Failed to end impersonation:', error);
      alert('Failed to end impersonation.');
    }
  };

  const viewLogDetails = async (logId: string) => {
    try {
      const log = await impersonationService.getLog(logId);
      setSelectedLog(log);
      setShowLogModal(true);
    } catch (error) {
      console.error('Failed to load log details:', error);
    }
  };

  const filteredLogs = logs.filter(log => {
    const search = searchTerm.toLowerCase();
    return (
      log.admin?.username?.toLowerCase().includes(search) ||
      log.targetUser?.username?.toLowerCase().includes(search) ||
      log.reason?.toLowerCase().includes(search) ||
      log.ipAddress?.toLowerCase().includes(search)
    );
  });

  const formatDuration = (start: string | Date, end?: string | Date) => {
    const startTime = typeof start === 'string' ? new Date(start).getTime() : start.getTime();
    const endTime = end ? (typeof end === 'string' ? new Date(end).getTime() : end.getTime()) : Date.now();
    const duration = endTime - startTime;
    const minutes = Math.floor(duration / 60000);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m`;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ShieldCheckIcon className="h-10 w-10 text-purple-600 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Impersonation Logs</h1>
                <p className="text-gray-600 mt-1">Monitor admin user impersonation sessions</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={loadData}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Refresh
              </button>
              <button
                onClick={() => setShowStartModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
              >
                <PlayIcon className="h-4 w-4 mr-2" />
                Start Impersonation
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Total Sessions</p>
            <p className="text-2xl font-bold text-gray-900">{logs.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Active Now</p>
            <p className="text-2xl font-bold text-green-600">{activeSessions.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Today</p>
            <p className="text-2xl font-bold text-blue-600">
              {logs.filter(log => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                return new Date(log.startedAt) >= today;
              }).length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">This Week</p>
            <p className="text-2xl font-bold text-purple-600">
              {logs.filter(log => {
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);
                return new Date(log.startedAt) >= weekAgo;
              }).length}
            </p>
          </div>
        </div>

        {/* Active Sessions */}
        {activeSessions.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Active Sessions</h2>
            <div className="space-y-3">
              {activeSessions.map((session) => (
                <div key={session.id} className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center text-sm">
                        <UserCircleIcon className="h-5 w-5 text-gray-600 mr-1" />
                        <span className="font-medium text-gray-900">{session.admin?.username}</span>
                        <ArrowRightIcon className="h-4 w-4 mx-2 text-gray-400" />
                        <span className="font-medium text-green-600">{session.targetUser?.username}</span>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ACTIVE
                      </span>
                      <span className="text-xs text-gray-500">
                        Duration: {formatDuration(session.startedAt)}
                      </span>
                    </div>
                    <button
                      onClick={() => handleEndImpersonation(session.id)}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-red-600 hover:bg-red-700"
                    >
                      <StopIcon className="h-3 w-3 mr-1" />
                      End Session
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search by admin, target user, reason, or IP address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>

        {/* Logs Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Admin User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reason
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Duration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IP Address
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center">
                    <ArrowPathIcon className="h-6 w-6 text-gray-400 animate-spin mx-auto" />
                    <span className="ml-2 text-gray-600">Loading logs...</span>
                  </td>
                </tr>
              ) : filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center">
                    <ShieldCheckIcon className="h-12 w-12 text-gray-400 mx-auto" />
                    <p className="mt-2 text-sm text-gray-600">No impersonation logs found</p>
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <UserCircleIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">
                          {log.admin?.username || 'Unknown'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <UserCircleIcon className="h-5 w-5 text-purple-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">
                          {log.targetUser?.username || 'Unknown'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-900 max-w-xs truncate">{log.reason}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <ClockIcon className="h-4 w-4 mr-1 text-gray-400" />
                        {formatDuration(log.startedAt, log.endedAt)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(log.startedAt).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 font-mono">{log.ipAddress || 'N/A'}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => viewLogDetails(log.id)}
                        className="text-purple-600 hover:text-purple-900 font-medium"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Start Impersonation Modal */}
        {showStartModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowStartModal(false)} />
              
              <div className="inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full">
                <form onSubmit={handleStartImpersonation}>
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Start Impersonation</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Target User ID *</label>
                        <input
                          type="text"
                          required
                          value={startForm.targetUserId}
                          onChange={(e) => setStartForm(prev => ({ ...prev, targetUserId: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                          placeholder="Enter user ID"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                        <textarea
                          required
                          value={startForm.reason}
                          onChange={(e) => setStartForm(prev => ({ ...prev, reason: e.target.value }))}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                          placeholder="Why are you impersonating this user?"
                        />
                      </div>

                      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                        <p className="text-xs text-yellow-800">
                          ⚠️ <strong>Warning:</strong> All actions performed during impersonation are logged.
                          Use this feature responsibly and only when necessary.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end gap-2">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                    >
                      Start Impersonation
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowStartModal(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* View Log Details Modal */}
        {showLogModal && selectedLog && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowLogModal(false)} />
              
              <div className="inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-2xl sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Impersonation Session Details</h3>
                  
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Admin User</p>
                        <p className="text-sm text-gray-900">{selectedLog.admin?.username}</p>
                        <p className="text-xs text-gray-500">{selectedLog.admin?.email}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Target User</p>
                        <p className="text-sm text-gray-900">{selectedLog.targetUser?.username}</p>
                        <p className="text-xs text-gray-500">{selectedLog.targetUser?.email}</p>
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium text-gray-500">Reason</p>
                      <p className="text-sm text-gray-900">{selectedLog.reason}</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Start Time</p>
                        <p className="text-sm text-gray-900">{new Date(selectedLog.startedAt).toLocaleString()}</p>
                      </div>
                      {selectedLog.endedAt && (
                        <div>
                          <p className="text-sm font-medium text-gray-500">End Time</p>
                          <p className="text-sm text-gray-900">{new Date(selectedLog.endedAt).toLocaleString()}</p>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium text-gray-500">Duration</p>
                      <p className="text-sm text-gray-900">{formatDuration(selectedLog.startedAt, selectedLog.endedAt)}</p>
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium text-gray-500">IP Address</p>
                      <p className="text-sm text-gray-900 font-mono">{selectedLog.ipAddress || 'N/A'}</p>
                    </div>
                    
                    {selectedLog.actionsPerformed && selectedLog.actionsPerformed.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-500 mb-2">Actions Performed</p>
                        <div className="bg-gray-50 rounded p-3 max-h-60 overflow-y-auto">
                          <ul className="space-y-1">
                            {selectedLog.actionsPerformed.map((action, idx) => (
                              <li key={idx} className="text-xs text-gray-700">• {action}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end">
                  <button
                    onClick={() => setShowLogModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminImpersonationLogsPage;
